import {
  Flex,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Tag,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { DefaultScreenTileBox } from '~/common/components/defaultUIComponents/DefaultScreenTileBox';
import CompanySettingsApi from '~/common/api/zeiterfassungServerApi/CompanySettingsApi';
import { AggregatedOrderedProject } from '~/common/api/orderingServiceApi/OrderingApi';
import { LabeledText } from '~/common/components/defaultUIComponents/LabeledText';
import { getDateOrKW, getDateWithoutTime } from '~/common/utils/timeUtils';
import { Button } from '~/common/components/defaultUIComponents/Button';
import { DeleteOrderedProjectModal } from './DeleteOrderedProjectModal';
import { OrderProjectHistoryModal } from './OrderProjectHistoryModal';
import { Text } from '~/common/components/defaultUIComponents/Text';
import { EditOrderedProjectModal } from './EditOrderedProjectModal';
import { BellIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import useTranslate from '~/common/utils/translate';
import { useMemo } from 'react';

export function OrderedProjectSummary(props: {
  orderedProject: AggregatedOrderedProject;
}) {
  const orderedProject = props.orderedProject;
  const order = props.orderedProject.orderInformation;
  const t = useTranslate();

  const [{ data: companySettings }] =
    CompanySettingsApi.hooks.useCompanySettings();

  const { isOpen: isDeleteOpen, onToggle: toggleDeleteOpen } = useDisclosure();
  const { isOpen: historyOpen, onToggle: toggleHistoryOpen } = useDisclosure();
  const { isOpen: isEditOpen, onToggle: toggleEditOpen } = useDisclosure();

  const historyWasUpdated = useMemo(() => {
    return !orderedProject.checkedIn && orderedProject.doneAmount > 0;
  }, [orderedProject]);

  const finished = useMemo(() => {
    return orderedProject.doneAmount >= orderedProject.amount;
  }, [orderedProject]);

  return (
    <DefaultScreenTileBox borderBottom="1px solid gray" pb={1}>
      <DeleteOrderedProjectModal
        orderedProjectId={orderedProject.id}
        open={isDeleteOpen}
        toggle={toggleDeleteOpen}
      />
      <HStack>
        <Grid templateColumns="repeat(6, 1fr)" columnGap={5} width="70%">
          {/* first line */}
          <GridItem colSpan={2}>
            <LabeledText
              color="primary"
              textDecoration="underline"
              label="ordertile.orderProject.company.name.text"
              mr={1}
            >
              {order.customerName}
            </LabeledText>
          </GridItem>
          <GridItem colSpan={2}>
            <Flex>
              <Text fontWeight="semibold" mr={1}>
                ordertile.orderProject.amount.text
              </Text>
              <Text>{orderedProject.amount}</Text>
            </Flex>
          </GridItem>
          <GridItem colSpan={2}>
            <Flex>
              <Text fontWeight="semibold" mr={1}>
                ordertile.orderProject.inWeekplan.text
              </Text>
              <Text>
                {orderedProject.priority ??
                  'ordertile.orderProject.NotinWeekplan.text'}
              </Text>
            </Flex>
          </GridItem>
          {/* second line */}
          <GridItem colSpan={2}>
            <LabeledText
              label="ordertile.orderProject.issue_date.text"
              width="90px"
            >
              {getDateWithoutTime(new Date(order.orderedDate))}
            </LabeledText>
          </GridItem>
          <GridItem colSpan={2}>
            {order.dueDate && (
              <LabeledText
                label="ordertile.orderProject.due_date.text"
                width="90px"
              >
                {getDateOrKW(
                  new Date(order.dueDate),
                  companySettings?.order_kw_instead_of_date,
                )}
              </LabeledText>
            )}
          </GridItem>
          <GridItem colSpan={2}>
            {orderedProject.comment && (
              <LabeledText label="ordertile.comment">
                {orderedProject.comment}
              </LabeledText>
            )}
          </GridItem>
          <GridItem colSpan={6}>
            {(orderedProject.options?.length ?? 0) > 0 ? (
              <HStack>
                <Text fontWeight="semibold" fontSize="md">
                  ordertile.options.text
                </Text>
                {orderedProject.options?.map((option, index) => (
                  <Tag size="sm" key={index} variant="solid" colorScheme="gray">
                    {option.project.name}
                  </Tag>
                ))}
              </HStack>
            ) : null}
          </GridItem>
        </Grid>
        <Tooltip
          label={t('ordertile.button.editOrder.text')}
          aria-label={t('ordertile.button.editOrder.text')}
        >
          <IconButton
            icon={<EditIcon />}
            onClick={() => toggleEditOpen()}
            aria-label={t('ordertile.button.editOrder.text') as string}
            isRound
            mr={1}
          />
        </Tooltip>
        <EditOrderedProjectModal
          orderedProject={orderedProject}
          isOpen={isEditOpen}
          close={toggleEditOpen}
        />
        <Tooltip
          label={t('ordertile.button.delete.text')}
          aria-label={t('ordertile.button.delete.text') as string}
        >
          <IconButton
            icon={<DeleteIcon />}
            onClick={toggleDeleteOpen}
            aria-label={t('ordertile.button.delete.text') as string}
            isRound
            mr={1}
          />
        </Tooltip>

        <Button
          onClick={toggleHistoryOpen}
          color={historyWasUpdated ? 'red.600' : 'white'}
        >
          {t('ordertile.orderProject.doneAmount.text')}{' '}
          {orderedProject.doneAmount}
          {historyWasUpdated && <BellIcon ml={2} color="red.600" />}
        </Button>
        <OrderProjectHistoryModal
          orderedProjectId={orderedProject.id}
          history={orderedProject.history}
          checkedIn={orderedProject.checkedIn}
          isOpen={historyOpen}
          onToggle={toggleHistoryOpen}
          finish={finished}
        />
      </HStack>
    </DefaultScreenTileBox>
  );
}
